import { Platform } from 'models/platforms/platform';
import { AbstractDeviceIntegration } from 'config/platforms/AbstractDeviceIntegration';
import {
  translateCcSettingToCss,
  CcEdgeType,
} from 'config/platforms/deviceIntegration';
import { percentToHex } from 'support/generalUtils';
import {
  Accessibility,
  Advertising,
  Device,
  Discovery,
  Lifecycle,
  Metrics,
  Parameters,
} from '@firebolt-js/sdk';
import { Manufacturer } from 'models/platforms/manufacturer';
import { Metadata, Registry } from '@lightningjs/sdk';
import { RoutePath } from 'config/routes';
import { navigateHome } from 'support/routerUtils';
import {
  ErrorCode,
  ReportableError,
} from 'services/analytics/reportingServicePage';
import { ComcastKeyMapping } from 'models/platforms/comcastKeyMapping';
import { OneTrustRemoteKeys } from 'types/oneTrust';
import { createDeepLinkUrl, DeepLinkUrlArgs } from 'support/deepLinkUtils';

const CANVAS_REFRESH_INTERVAL = 5000;
const CANVAS_REFRESH_BUFFER = 1000;
const NETWORK_POLLING_INTERVAL = 5000;

const LOW_SPEC_MODELS = ['XI5'];

export class ComcastDeviceIntegration extends AbstractDeviceIntegration {
  protected platform = Platform.COMCAST;

  /** Fetched on platform `load()` */
  private appId = '';
  private deepLinkInitialized = false;
  private intentPayload: Parameters.NavigationIntent | undefined;
  private announcerChangeCallback: ((arg: boolean) => void) | null = null;
  private _shouldAnnounceContext = true;
  private ccChangeCallback: ((enabled: boolean) => void) | null = null;
  private _canvasRefreshIntervalId: number | null = null;
  private _networkChangeCallback: ((arg: boolean) => void) | null = null;
  private _networkPollingIntervalId: number | null = null;
  private _previousNetworkState: boolean | null = null;

  constructor() {
    super();
    this.deviceInfo.manufacturer = Manufacturer.COMCAST;
  }

  override getKeyMapping() {
    return ComcastKeyMapping;
  }

  override shouldCloseOnExit(event: KeyboardEvent): boolean {
    // 'E' key has the key code conflict with Exit button for Comcast Flex (code: 69).
    // Guard the unintentional app exit on input.
    return event.key.toLowerCase() === 'exit';
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  getPerformanceMode(): boolean {
    return false;
  }

  getAppId(): string {
    return this.appId;
  }

  hasFlickerIssue(): boolean {
    return false;
  }

  isLowPerformanceModel(): boolean {
    return LOW_SPEC_MODELS.includes(this.deviceInfo.model);
  }

  isLowMemoryModel(): boolean {
    return LOW_SPEC_MODELS.includes(this.deviceInfo.model);
  }

  getSubmittedAppVersion(): string {
    return '';
  }

  override getSeekRate() {
    return [2, 6, 12, 30];
  }

  async load() {
    try {
      const appVersion = Metadata.appVersion() ?? 'Unknown';
      await Metrics.appInfo(appVersion);
    } catch (e) {
      // Metrics.appInfo() is unavailable on Firebolt <1.3.0
      this.reportError({
        error: e,
        userFacing: false,
        code: ErrorCode.c37,
      });
    }

    /**
     * On Firebolt mock, device changes are made using:
     * `node cli.mjs --upload {FILE_PATH}`
     */
    const [init, versions, model, deviceId, appId, advertisingId] =
      await Promise.all([
        Parameters.initialization(), // Mandatory Lifecycle method
        Device.version(),
        Device.model(),
        Device.uid(),
        Advertising.appBundleId(),
        Advertising.advertisingId(),
      ]);

    this.intentPayload = init.discovery?.navigateTo;

    this.deviceInfo.ip = ''; // Device IP address is not available from Firebolt SDK
    this.deviceInfo.model = model;

    // Prevent default back key for Xi5 (Flex)
    if (model === 'XI5') {
      document.body.onkeydown = e => {
        if (e.which === 8) {
          e.preventDefault();
        }
      };
    }

    const { major, minor, patch } = versions.firmware;
    const osVersion = `${major}.${minor}.${patch}`;
    this.deviceInfo.osVersion = osVersion;

    const { ifa, ifa_type, lmt } = advertisingId;
    this.deviceInfo.adId = ifa;
    this.deviceInfo.isLat = lmt === '1';
    this.deviceInfo.ifaType = ifa_type;

    this.deviceId = deviceId;
    this.appId = appId;

    Advertising.listen('policyChanged', data => {
      const { limitAdTracking } = data;
      this.deviceInfo.isLat = !!limitAdTracking;

      if (this.privacyChangeListener)
        this.privacyChangeListener(!!limitAdTracking);
    });

    Discovery.listen('navigateTo', intent => {
      this.intentPayload = intent;
      if (this.deepLinkInitialized) this.parseDeepLink();
    });

    // Load current device CC styles
    const ccSettings = await Accessibility.closedCaptionsSettings();
    this.updateCCSetting(ccSettings);

    // Load device announcer setting
    const vgSettings = await Accessibility.voiceGuidanceSettings();
    this.updateAnnouncerSettings(vgSettings);

    // Set network status polling
    this._networkPolling();

    return Promise.resolve();
  }

  override onAppActive() {
    // We have to call `Lifecycle.ready()` when the first is first active.
    // For more information, see https://docs.developer.comcast.com/docs/hello-world-guide
    Lifecycle.ready();

    /**
     * Uncomment the listeners below to listen to device events
     * On Firebolt mock, these will listen for any event calls made with:
     * `node cli.mjs --event {FILE_PATH}`
     *
     * Try running `node cli.mjs --event ../examples/accessibility-onClosedCaptionsSettingsChanged1.event.json`
     * to see a `closedCaptionsSettingsChanged` event
     */
    Accessibility.listen(async (e, data) => {
      switch (e as Accessibility.Event) {
        case 'closedCaptionsSettingsChanged':
          // change cc settings
          // do not use the data from this event, it doesn't contain the styles.
          // fetch caption settings manually
          this.updateCCSetting(await Accessibility.closedCaptionsSettings());
          break;
        case 'voiceGuidanceSettingsChanged':
          this.updateAnnouncerSettings(
            data as Accessibility.VoiceGuidanceSettings,
          );
          break;
        default:
          break;
      }
    });
    // Device.listen((e, data) => {
    //   console.group(`===== [Device] ${e} =====`);
    //   console.log('data', data);
    //   console.groupEnd();
    // });
    // Discovery.listen((e, data) => {
    //   console.group(`===== [Discovery] ${e} =====`);
    //   console.log('data', data);
    //   console.groupEnd();
    // });
    // Lifecycle.listen((e, data) => {
    //   console.group(`===== [Lifecycle] ${e} =====`);
    //   console.log('data', data);
    //   console.groupEnd();
    // });
  }

  beforeAppClose(callback: (showPopup: boolean) => void) {
    callback(true);
  }

  closeApp() {
    // Inform platform that the app is ready to be closed
    Lifecycle.close(Lifecycle.CloseReason.USER_EXIT);

    // Cleanup
    Advertising.clear();
    Device.clear();
    this.clearPrivacyChangeListener();

    // Clear network polling interval
    if (this._networkPollingIntervalId !== null) {
      Registry.clearInterval(this._networkPollingIntervalId);
      this._networkPollingIntervalId = null;
      this._previousNetworkState = null;
    }

    // Inform platform that the app is done unloading
    Lifecycle.finished();
  }

  handleDeepLink() {
    this.deepLinkInitialized = true;
    return this.parseDeepLink();
  }

  override handleNetworkChange(callback: (arg: boolean) => void) {
    // Store callback, _networkPolling() will handle calling it
    this._networkChangeCallback = callback;
  }

  setScreenSaver(state: boolean) {
    return;
  }

  getAnnouncerEnabled() {
    return false;
  }

  shouldAnnounceContext(): boolean {
    // Toggle announceContext based on 'navigationHints' setting
    return this._shouldAnnounceContext;
  }

  shouldAnnounceSeeking(): boolean {
    return true;
  }

  handleAnnouncerChange(callback: (arg: boolean) => void): void {
    this.announcerChangeCallback = callback;
  }

  handleCcChange(callback: (enabled: boolean) => void): void {
    this.ccChangeCallback = callback;
  }

  useTextTrackComponent(): boolean {
    return true;
  }

  onAdPodStart(): void {
    this._canvasRefresh(true);
  }

  onAdPodEnd(): void {
    // Buffer to keep interval active after ads
    Registry.setTimeout(() => {
      this._canvasRefresh(false);
    }, CANVAS_REFRESH_BUFFER);
  }

  onPlayerExit(): void {
    this._canvasRefresh(false);
  }

  override reportError(reportableError: ReportableError) {
    const { error, userFacing, code } = reportableError;
    const message =
      error instanceof Object && 'message' in error
        ? `${error.message}`
        : `${error}`;

    Metrics.error(Metrics.ErrorType.OTHER, code, message, userFacing);
  }

  private getFontSize(fontScale: number | undefined) {
    // Comcast uses font scale (0.5 - 2). We need to set pixel sizes.
    switch (fontScale) {
      case 0.5: // Small
        return 30;
      case 1: // Medium
        return 40;
      case 1.5: // Large
        return 50;
      case 2: // Extra Large
        return 60;
      default:
        return 40;
    }
  }

  private updateCCSetting(data: Accessibility.ClosedCaptionsSettings) {
    this._ccSettings.enabled = data.enabled;

    if (data.styles) {
      this._ccSettings.fontFace = data.styles.fontFamily;
      this._ccSettings.fontSize = this.getFontSize(data.styles.fontSize);
      this._ccSettings.color = data.styles.fontColor;

      // Map ClosedCaptionsStyles.FontEdge
      let edgeType: CcEdgeType | undefined;
      switch (data.styles.fontEdge) {
        case 'none':
          edgeType = undefined;
          break;
        case 'raised':
          edgeType = '-2px -2px 2px';
          break;
        case 'depressed':
          edgeType = '2px 2px 2px';
          break;
        case 'uniform':
          edgeType = '0px 0px 4px';
          break;
        case 'drop_shadow_left':
          edgeType = '-4px -4px 4px';
          break;
        case 'drop_shadow_right':
          edgeType = '4px 4px 4px';
          break;
      }
      this._ccSettings.edgeType = edgeType;
      this._ccSettings.edgeColor = data.styles.fontEdgeColor;

      this._ccSettings.colorOpacity = data.styles.fontOpacity
        ? percentToHex(data.styles.fontOpacity)
        : undefined;

      this._ccSettings.backgroundColor = data.styles.backgroundColor;
      this._ccSettings.backgroundOpacity = data.styles.backgroundOpacity
        ? percentToHex(data.styles.backgroundOpacity)
        : undefined;

      // Fire callback to update TextTrackDisplay
      this.ccChangeCallback?.(data.enabled);
    }

    // This CSS won't be used by Comcast as it will only show captions via TextTrackDisplay.
    // But since native tracks still show up on browser, hide them by setting fontSize 0.
    const style =
      document.getElementById('caption-styles') ??
      document.createElement('style');
    style.id = 'caption-styles';

    const hiddenCcSettings = {
      ...this._ccSettings,
      fontSize: 0,
    };

    style.innerHTML = translateCcSettingToCss(hiddenCcSettings);
    document.head.appendChild(style);
  }

  private parseDeepLink(): boolean {
    const intent = this.intentPayload;
    if (!intent || !intent.action) return false;

    switch (intent.action) {
      case 'entity':
        // Navigate to details page
        return this.handleEntityDeepLink(intent.data);
      case 'play-entity':
        // Navigate to playback
        return this.handlePlayableEntityDeepLink(intent.data.entity);
      case 'playback':
        // Navigate to playback
        return this.handlePlayableEntityDeepLink(intent.data);
      case 'search': {
        // Navigate to search
        const url = createDeepLinkUrl(RoutePath.SEARCH, '');
        const query = intent.data?.query;
        this.handleDeepLinkURL(url, { query, reload: true });
        return true;
      }
      case 'section':
        // Navigate to section
        this.handleDeepLinkURL(intent.data.sectionName);
        return true;
      case 'tune': {
        // Navigate to channel
        const url = createDeepLinkUrl(
          RoutePath.EPG,
          intent.data.entity.entityId,
        );
        this.handleDeepLinkURL(url);
        return true;
      }
      case 'home':
      case 'launch':
      case 'play-query': // Unsupported
      default:
        // Navigate home
        navigateHome();
        return true;
    }
  }

  private handleEntityDeepLink(entity: Discovery.Entity): boolean {
    // Cannot handle un-typed entity
    if (!entity.entityId || !('entityType' in entity)) return false;

    const { entityId, entityType } = entity;
    switch (entityType) {
      case 'channel': {
        const url = createDeepLinkUrl(RoutePath.EPG, entityId);
        this.handleDeepLinkURL(url);
        return true;
      }
      case 'program':
        return this.handleProgramDeepLink(entity, false);
      case 'music': // Unsupported
      case 'playlist': // Unsupported
      default:
        return false;
    }
  }

  private handleProgramDeepLink(
    entity: Discovery.ProgramEntity,
    play: boolean,
  ): boolean {
    const { assetId, entityId, programType } = entity;

    let route: RoutePath | null = null;
    let id: string | undefined = '';
    const args: DeepLinkUrlArgs = {};

    switch (programType) {
      case 'episode': {
        if (play) {
          route = RoutePath.PLAY;
          id = entityId;
          break;
        }
        route = RoutePath.SERIES_DETAIL;
        id = entity.seriesId;
        args.season = entity.seasonId;
        break;
      }
      case 'extra': {
        if (play) {
          route = RoutePath.PLAY;
          id = assetId;
          break;
        }
        route = RoutePath.SERIES_DETAIL;
        id = entityId;
        break;
      }
      case 'movie': {
        if (play) {
          route = RoutePath.PLAY;
          id = assetId;
          break;
        }
        route = RoutePath.MOVIE_DETAIL;
        id = entityId;
        break;
      }
      case 'season': {
        route = RoutePath.SERIES_DETAIL;
        id = entity.seriesId;
        args.season = entityId;
        break;
      }
      case 'series': {
        route = RoutePath.SERIES_DETAIL;
        id = entityId;
        break;
      }
      case 'sportingEvent': {
        route = RoutePath.LIVE_EVENT_DETAIL;
        id = entityId;
        break;
      }
      // Unsupported
      case 'advertisement':
      case 'concert':
      case 'minisode':
      case 'musicVideo':
      case 'other':
      case 'preview':
      default:
        return false;
    }

    if (!route || !id) return false;

    const deepLinkUrl = createDeepLinkUrl(route, id, args);
    this.handleDeepLinkURL(deepLinkUrl);

    return true;
  }

  private handlePlayableEntityDeepLink(
    entity: Discovery.PlayableEntity,
  ): boolean {
    switch (entity.entityType) {
      case 'program': {
        return this.handleProgramDeepLink(entity, true);
      }
      case 'playlist': // Unsupported
      case 'music': // Unsupported
      default:
        return false;
    }
  }

  private updateAnnouncerSettings(data: Accessibility.VoiceGuidanceSettings) {
    const { enabled, navigationHints, rate } = data;
    this.announcerChangeCallback?.(enabled);
    this._shouldAnnounceContext = navigationHints;
  }

  private _canvasRefresh(enable: boolean) {
    if (enable) {
      this._canvasRefreshIntervalId = Registry.setInterval(() => {
        /*
        On comcast, whenever a new canvas is created, screen size is adjusted to the new canvas size.
        During ads, it seems to create a canvas within iframe which causes the screen size to shrink.
        As a workaround, we continuously create an empty canvas with original size.
        */
        this.resizeCanvas();
      }, CANVAS_REFRESH_INTERVAL);
    } else {
      Registry.clearInterval(this._canvasRefreshIntervalId);
      // Resize canvas again to be safe
      this.resizeCanvas();
    }
  }

  private resizeCanvas() {
    const canvas = document.createElement('canvas');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    canvas.getContext('webgl');
  }

  // App doesn't seem to receive Firebolt's 'networkChanged' event when
  // device goes offline. Instead, we use polling to detect status changes
  private _networkPolling() {
    this._networkPollingIntervalId = Registry.setInterval(async () => {
      try {
        const networkInfo = await Device.network();
        const isOnline = networkInfo.state === Device.NetworkState.CONNECTED;

        // Only call callback if state changed
        if (this._previousNetworkState !== isOnline) {
          this._previousNetworkState = isOnline;
          this._networkChangeCallback?.(isOnline);
        }
      } catch (e) {
        // When the device goes offline, this catch block will be called
        if (this._previousNetworkState !== false) {
          if (e === 'ParseError') {
            this._networkChangeCallback?.(false);
          } else {
            reportError({
              error: e,
              userFacing: false,
              code: ErrorCode.c44,
            });
          }
        }
        this._previousNetworkState = false;
      }
    }, NETWORK_POLLING_INTERVAL);
  }
}
